import { useCallback, useEffect, useRef } from 'react';

import { apiFetchUserByEmail } from '#/api/apiFetchUserByEmail';
import { type ListingApplicationPayload, validateApplication } from '#/api/ats';
import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type {
  ApiInternshipId,
  ApiJobId,
  ApiListingApplicationId,
  ApiVolopId,
} from '#/api/types/ApiTypedId';
import type { FormSubmitFunctionData } from '#/components/Form/FormContext';
import { Text } from '#/components/Text/Text';
import { useTmpStorageContext } from '#/contexts/TmpStorageContext';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import { userDashboardApiClientFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { LISTING_TYPE_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypeHumanNameMap';
import { showModal } from '#/zustand-stores/modalStore';

type Args = {
  listingId: ApiJobId | ApiInternshipId | ApiVolopId;
  listingType: ApiListingApplicationListingType;
  applied: boolean;
  onApplied: (listingApplicationId: ApiListingApplicationId) => void;
};

export function useListingApplicationSubmissionAsGuest({
  listingId,
  listingType,
  applied,
  onApplied,
}: Args) {
  const { i18n } = useI18n();
  const { saveTemporaryStorage } = useTmpStorageContext();

  const { user } = useUser();
  const [appliedAsGuest, setAppliedAsGuest] = useKeyedState(false, {
    key: listingId,
  });

  const onAppliedRef = useLatest(onApplied);
  const totalFetchesRef = useRef(0);

  useEffect(() => {
    if (applied || !appliedAsGuest || !user?.emailVerified) return;

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const interval = setInterval(async () => {
      // If it takes longer than a minute after the user verified his email
      // probably something went wrong with the application
      if (totalFetchesRef.current > 60) {
        setAppliedAsGuest(false);
        return;
      }

      totalFetchesRef.current = totalFetchesRef.current + 1;

      const listingExtras = await userDashboardApiClientFetchListingExtras([
        listingId,
      ]);
      const newApplicationId = listingExtras[listingId]?.applicationId;

      if (newApplicationId) {
        onAppliedRef.current(newApplicationId);
        clearInterval(interval);
      }
    }, 1_000);

    return () => clearInterval(interval);
  }, [
    applied,
    listingId,
    appliedAsGuest,
    user?.emailVerified,
    onAppliedRef,
    setAppliedAsGuest,
  ]);

  const submitListingApplicationAsGuest = useCallback(
    async ({
      values,
      updateSubmitting,
    }: FormSubmitFunctionData<ListingApplicationPayload>) => {
      await validateApplication(values);

      const [id, userExists] = await Promise.all([
        saveTemporaryStorage?.([
          { type: 'LISTING_APPLICATION_VALUES', data: values },
        ]),
        values.email && apiFetchUserByEmail(values.email),
      ]);

      setAppliedAsGuest(true);

      updateSubmitting(false);
      const redirectTo = `${window.location.pathname}${
        window.location.search
      }${window.location.search ? '&' : '?'}tmpId=${id ?? ''}`;

      if (userExists) {
        showModal('LOGIN', { redirectTo, email: values.email });
        return;
      }

      showModal('SIGNUP', {
        title: i18n('Just One More Step!'),
        description: (
          <Text mb={4}>
            {i18n(
              'Create an account on Idealist in order to finish applying to this %(listingType)s.',
              { listingType: LISTING_TYPE_HUMAN_NAME_MAP(i18n)[listingType] },
            )}
          </Text>
        ),
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        redirectTo,
      });
    },
    [i18n, listingType, saveTemporaryStorage, setAppliedAsGuest],
  );

  return {
    submitListingApplicationAsGuest,
  };
}
